import { useMemo } from "react";
import { useAuthenticated } from "../auth";
import { usePortalApi } from "./portal-api";
import {
  type SyncModule,
  useSyncData,
  useSyncModuleRegistration,
} from "../sync";
import { daily } from "./provider";

const syncModuleId = "abschnittsvorlagen";

export type AbschnittsvorlageElement = {
  readonly id: string;
  readonly name: string;
  readonly order: number;
  readonly funkrufgruppeId?: string;
  readonly mitPatienten?: boolean;
};

export type Abschnittsvorlage = {
  readonly id: string;
  readonly name: string;
  readonly elemente: ReadonlyArray<AbschnittsvorlageElement>;
};

function useAbschnittsvorlagenSyncModule(): SyncModule<
  ReadonlyArray<Abschnittsvorlage>
> {
  const { portalKey } = useAuthenticated();
  const { useNewApi, baseUrl } = usePortalApi();

  return useMemo(
    () => ({
      id: syncModuleId,
      i18nKey: `tasks:${syncModuleId}`,
      defaultOptions: { schedule: { type: "periodic", interval: daily } },
      // Section templates are a new-Datenportal-only resource — the legacy
      // portal has no equivalent endpoint, so we simply don't fetch anything for it.
      fetchData: useNewApi && baseUrl
        ? () => fetchAbschnittsvorlagenNew(portalKey, baseUrl)
        : async () => [],
    }),
    [portalKey, useNewApi, baseUrl],
  );
}

async function fetchAbschnittsvorlagenNew(
  portalKey: string,
  baseUrl: string,
): Promise<ReadonlyArray<Abschnittsvorlage>> {
  const response = await fetch(
    `${baseUrl}/api/device/sync/section-templates`,
    {
      headers: { Authorization: `Bearer ${portalKey}` },
    },
  );

  if (!response.ok) {
    throw new Error(
      `Error fetching data: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();
  return (data.sectionTemplates ?? []).map(parseAbschnittsvorlageNew);
}

function parseAbschnittsvorlageNew(entry: any): Abschnittsvorlage {
  return {
    id: entry.id,
    name: entry.name,
    elemente: (entry.items ?? [])
      .map((item: any) => ({
        id: item.id,
        name: item.name,
        order: item.order ?? 0,
        funkrufgruppeId: item.radioCallGroupId || undefined,
        mitPatienten: item.withPatients ?? false,
      }))
      .sort(
        (a: AbschnittsvorlageElement, b: AbschnittsvorlageElement) =>
          a.order - b.order,
      ),
  };
}

export function useAbschnittsvorlagenSync() {
  const module = useAbschnittsvorlagenSyncModule();
  useSyncModuleRegistration(module);
}

export function useAbschnittsvorlagen(): ReadonlyArray<Abschnittsvorlage> {
  return useSyncData(syncModuleId) ?? [];
}
